import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Share as ShareIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { contentService } from '../../services';
import AiSummaryModal from './AiSummaryModal';
import { ContentTile } from './ContentTile';
import { ContentRow } from './ContentRow';
import { ViewMode } from './ViewModeToggle';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../utils/permissions';

interface ContentCardProps {
  content: any;
  viewMode: ViewMode;
  showQuickActions?: boolean;
  onAction?: (action: string, content: any) => void;
  loading?: boolean;
  bookmarked?: boolean;
}

const statusMap: Record<number, string> = {
  0: 'draft',
  1: 'draft',
  2: 'private',
  3: 'public',
  4: 'archived',
};

const ContentCard: React.FC<ContentCardProps> = ({
  content,
  viewMode,
  showQuickActions = true,
  onAction,
  loading = false,
  bookmarked = false,
}) => {
  const navigate = useNavigate();
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  const [aiSummaryData, setAiSummaryData] = useState<any>(undefined);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.CONTENT_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.CONTENT_DELETE);
  const canDownload = hasPermission(PERMISSIONS.CONTENT_DOWNLOAD);
  const canShare = hasPermission(PERMISSIONS.CONTENT_SHARE);

  const contentStatus =
    typeof content.status === 'number' ? statusMap[content.status] : content.status;
  const isPublicContent = contentStatus === 'public';
  const canActuallyShare = canShare && isPublicContent;
  const shareTooltip = !canShare
    ? 'You do not have permission to share content'
    : !isPublicContent
    ? 'Only public content can be shared via link'
    : 'Share';

  const contentTitle: string = content.title || content.name || '';

  const handleClick = useCallback(() => {
    onAction?.('view', content);
    // Plan B: route views to /views/:id (renderer), files to /content/:id.
    // The `kind` discriminator isn't carried through the Content proto, so
    // we derive view-ness from mimeType (which IS passed through and is the
    // user-facing discriminator per Plan B's design principle).
    const isView =
      content.kind === 'view' ||
      content.mimeType === 'application/vnd.sharp.view' ||
      content.mime_type === 'application/vnd.sharp.view';
    const target = isView ? `/views/${content.id}` : `/content/${content.id}`;
    navigate(target);
  }, [content, navigate, onAction]);

  const handleOpenAiSummary = useCallback(async () => {
    setAiSummaryData(undefined);
    setAiSummaryLoading(true);
    setAiSummaryOpen(true);
    try {
      const teamId = content.teamId || content.team_id || '';
      const fresh = await contentService.getContent(content.id, teamId, true);
      const fetched = fresh?.content || fresh;
      setAiSummaryData(fetched?.aiSummary ?? null);
    } catch {
      setAiSummaryData(null);
    } finally {
      setAiSummaryLoading(false);
    }
  }, [content]);

  // Grid hover-reveal action row: chunky Download button + AI Summary + Share.
  const gridActions = showQuickActions
    ? ([
        canDownload && (
          <Button
            key="download"
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              onAction?.('download', content);
            }}
            startIcon={<DownloadIcon sx={{ fontSize: 12 }} />}
            sx={(theme) => ({
              flex: 1,
              height: 30,
              fontSize: 11.5,
              borderRadius: '8px',
              borderColor: theme.palette.divider,
              color: theme.palette.text.secondary,
              '&:hover': {
                borderColor: theme.palette.brand.teal,
                color: theme.palette.brand.teal,
                background: 'transparent',
              },
            })}
          >
            Download
          </Button>
        ),
        <Tooltip key="ai-summary" title="AI Summary">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenAiSummary();
            }}
            sx={(theme) => ({
              width: 32,
              height: 30,
              borderRadius: '8px',
              border: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.secondary,
              '&:hover': {
                borderColor: theme.palette.brand.teal,
                color: theme.palette.brand.teal,
              },
            })}
          >
            <AutoAwesomeIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>,
        canShare && (
          <Tooltip key="share" title={shareTooltip}>
            <span>
              <IconButton
                size="small"
                disabled={!canActuallyShare}
                onClick={(e) => {
                  e.stopPropagation();
                  onAction?.('share', content);
                }}
                sx={(theme) => ({
                  width: 32,
                  height: 30,
                  borderRadius: '8px',
                  border: `1px solid ${theme.palette.divider}`,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    borderColor: theme.palette.brand.teal,
                    color: theme.palette.brand.teal,
                  },
                })}
              >
                <ShareIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </span>
          </Tooltip>
        ),
      ].filter(Boolean) as React.ReactNode[])
    : undefined;

  // List action column: plain icon buttons stacked vertically (matching the
  // original ContentCard list view quick-actions column).
  const listActions = showQuickActions
    ? ([
        canDownload && (
          <Tooltip key="download" title="Download">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onAction?.('download', content);
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
        canShare && (
          <Tooltip key="share" title={shareTooltip}>
            <span>
              <IconButton
                size="small"
                disabled={!canActuallyShare}
                onClick={(e) => {
                  e.stopPropagation();
                  onAction?.('share', content);
                }}
              >
                <ShareIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ),
      ].filter(Boolean) as React.ReactNode[])
    : undefined;

  // Shared kebab menu items: Download / Share / Edit / AI Summary / Delete.
  // Matches the original ContentCard menu in both grid and list modes.
  const kebabActions = showQuickActions
    ? ([
        canDownload && (
          <MenuItem key="download" onClick={() => onAction?.('download', content)}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>
        ),
        canShare && (
          <Tooltip key="share" title={shareTooltip} placement="left">
            <span>
              <MenuItem
                onClick={() => onAction?.('share', content)}
                disabled={!canActuallyShare}
              >
                <ListItemIcon>
                  <ShareIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Share</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        ),
        canEdit && (
          <MenuItem key="edit" onClick={() => onAction?.('edit', content)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        ),
        <MenuItem key="ai-summary" onClick={() => handleOpenAiSummary()}>
          <ListItemIcon>
            <AutoAwesomeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>AI Summary</ListItemText>
        </MenuItem>,
        canDelete && (
          <MenuItem key="delete" onClick={() => onAction?.('delete', content)}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        ),
      ].filter(Boolean) as React.ReactNode[])
    : undefined;

  // Bookmark button — pre-styled, passed as slot.
  const bookmarkNode = showQuickActions ? (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        onAction?.('bookmark', content);
      }}
      sx={(theme) => ({
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: bookmarked
          ? theme.palette.brand.teal
          : theme.palette.mode === 'light'
          ? 'rgba(255,255,255,.92)'
          : 'rgba(15,30,51,.92)',
        color: bookmarked ? '#fff' : theme.palette.text.primary,
        backdropFilter: 'blur(6px)',
        boxShadow: '0 1px 2px rgba(15,23,42,.06)',
        transition: 'transform 0.15s ease, background 0.15s ease',
        '&:hover': {
          background: bookmarked
            ? theme.palette.brand.teal
            : theme.palette.mode === 'light'
            ? 'rgba(255,255,255,.98)'
            : 'rgba(15,30,51,.98)',
          transform: 'scale(1.08)',
        },
      })}
    >
      {bookmarked ? (
        <BookmarkIcon sx={{ fontSize: 14 }} />
      ) : (
        <BookmarkBorderIcon sx={{ fontSize: 14 }} />
      )}
    </IconButton>
  ) : undefined;

  // Bookmark for the list view — a plain icon button (no banner-overlay chrome).
  const listBookmarkNode = showQuickActions ? (
    <Tooltip title={bookmarked ? 'Remove bookmark' : 'Bookmark'}>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onAction?.('bookmark', content);
        }}
      >
        {bookmarked ? (
          <BookmarkIcon fontSize="small" color="primary" />
        ) : (
          <BookmarkBorderIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  ) : undefined;

  if (viewMode === 'list') {
    return (
      <>
        <ContentRow
          content={content}
          onClick={handleClick}
          loading={loading}
          actions={listActions}
          kebabActions={kebabActions}
          bookmark={listBookmarkNode}
        />
        <AiSummaryModal
          open={aiSummaryOpen}
          onClose={() => setAiSummaryOpen(false)}
          title={contentTitle}
          aiSummary={aiSummaryData}
          loading={aiSummaryLoading}
        />
      </>
    );
  }

  return (
    <>
      <ContentTile
        content={content}
        variant="grid"
        onClick={handleClick}
        loading={loading}
        actions={gridActions}
        kebabActions={kebabActions}
        bookmark={bookmarkNode}
      />
      <AiSummaryModal
        open={aiSummaryOpen}
        onClose={() => setAiSummaryOpen(false)}
        title={contentTitle}
        aiSummary={aiSummaryData}
        loading={aiSummaryLoading}
      />
    </>
  );
};

export default ContentCard;
